import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react'
import Header from 'src/components//header/Header'
import { StateContext, DispatchContext } from '../../AppContext'
import { IState, IAddress } from 'src/Interface'
import styles from './order.module.scss'
import { baseApiURL, baseApiSocketURL } from 'src/utils/utils'
import DropDown from 'src/components/dropDown/Dropdown'
import DropDownInput from 'src/components/dropDownInput/DropDownInput'
import Input from 'src/components//input/Input'
import Button from 'src/components//button/Button'
import AutocompletePoi from 'src/components//autocomplete/AutocompletePoi'
import format from 'date-fns/format'
import axios from 'axios'
import { isValidNumberForRegion } from 'libphonenumber-js'
import { parse } from 'date-fns/esm'
import TimeInputComponent from 'src/components/timeInput/TimeInput'
import TextArea from 'src/components/textArea/TextArea'
import { add, differenceInMinutes } from 'date-fns'
import { toast } from 'react-toastify'
import useSocket from 'src/hooks/useSocket'
import da from 'date-fns/locale/da'
import useCancellablePromise from 'src/utils/useCancellablePromise'
import DatePickerComponent from '../../components/datePicker/DatePickerComponent'
import { gaEvenActions, gaEvent, gaEventCategories } from 'src/utils/ga/ga'
import { gaOrderedTripMapper, gaOrderTypes, gaUserMapper } from 'src/utils/ga/gaMapper'

interface IValues {
  numberOfPeople: string
  destinationAddress: IAddress
  pickupAddress: IAddress
  comment: string
  price: string
  fixedTripId: string
  project: string
  customerName: string
  attributes: Array<any>
  numberOfCars: string
  customerPhone: string
  reference: string
}

const Order: React.FunctionComponent = () => {
  const state: IState = useContext(StateContext)
  const orderRef = useRef(null)
  const socketData = useSocket(state.user.branchId && state.user.accessToken ? `${baseApiSocketURL()}/branches/${state.user.branchId}/orders?access_token=${state.user.accessToken}` : null)
  const [orderPending, setOrderPending] = useState(false)
  const redraw = useRef(null);
  const clicked = useRef(null);
  const b = useRef(null);
  const width = useRef(window.localStorage.getItem('width') || 720);
  const timeInterval = useRef(null)
  const orders = useRef(null)
  const userOrdersRef = useRef(state.userOrders)
  const dispatch: React.Dispatch<any> = useContext(DispatchContext)
  const [addresses, setAddresses] = useState([])
  const [accounts, setAccounts] = useState([])
  const [fixedTrips, setFixedTrips] = useState([])
  const [bookingModule, setBookingModule] = useState({
    attributes: [],
    fields: [],
    updatedAt: '',
    referenceLabel: ''
  })

  const { cancellablePromise } = useCancellablePromise()

  const [loading, setLoading] = useState(true)
  const [errors, setErrors] = useState({})
  const [validationErrors, setValidationErrors] = useState<any>({})
  const [selectedAttributes, updateSelectedAttributes] = useState([])
  const [sortOrders, setSortOrders] = useState(false);
  const [ordersLoaded, setOrdersLoaded] = useState(false)
  const [shouldUpdateTime, setShouldUpdateTime] = useState(true)
  const [completedPagination, setCompletedPagination] = useState({ page: 1, pageSize: 12, totalCount: 0, hasMore: false })
  const [loadingMoreCompleted, setLoadingMoreCompleted] = useState(false)
  const [accountId, setAccountId] = useState('')
  const [time, setTime] = useState(new Date())
  const [date, setDate] = useState(null)
  const [values, setValues] = useState<IValues>({
    fixedTripId: '',
    numberOfPeople: '4',
    destinationAddress: undefined,
    pickupAddress: null,
    comment: '',
    project: '',
    price: '',
    attributes: [],
    customerName: '',
    numberOfCars: '1',
    customerPhone: '',
    reference: '',
  })


  const updateTime = useCallback((date) => {
    clearTimeout(timeInterval.current);
    setTime(date)
    const remainingSeconds = 60 - date.getSeconds()
    timeInterval.current = window.setTimeout(() => updateTime(add(date, { seconds: remainingSeconds })), Number(remainingSeconds + '000'));
  }, [])

  const calc = useCallback(
    (e) => {
      if (orderRef.current) {
        b.current = orderRef.current.getBoundingClientRect();
        width.current = e.clientX - b.current.left;
      }
    },
    [],
  );

  useEffect(() => {
    if (!shouldUpdateTime) {
      clearTimeout(timeInterval.current);
    }
  }, [shouldUpdateTime])

  useEffect(() => {
    return () => {
      clearTimeout(timeInterval.current);
    }
  }, [])

  useEffect(() => {
    if (state.user.companyId && state.user.accessToken) {
      cancellablePromise(axios(`${baseApiURL()}/companies/${state.user.companyId}/booking_module`, {
        headers: {
          Authorization:
            `Bearer ${state.user.accessToken}`
        }
      }).then((result) => {
        updateSelectedAttributes(result.data.attributesFields.filter(attribute => attribute.required).map(attribute => attribute.id))
        setBookingModule({ attributes: result.data.attributesFields, fields: result.data.staticFields, updatedAt: result.data.updatedAt, referenceLabel: result.data.referenceLabel })
      }).catch((err) => {
        toast(err.response.data.message)
        setLoading(false)

      }))
    }
  }, [state.user.accessToken, state.user.companyId, cancellablePromise])

  useEffect(() => {
    if (ordersLoaded && bookingModule.fields.length) {
      setLoading(false)
    }
  }, [bookingModule.fields, ordersLoaded])

  // Keep ref in sync with state
  useEffect(() => {
    userOrdersRef.current = state.userOrders
  }, [state.userOrders])

  const fetchCompletedOrders = useCallback((page: number, pageSize: number, append: boolean = false) => {
    return cancellablePromise(axios(`${baseApiURL()}/orders?isIncludeOnlyOngoing=false&page=${page}&pageSize=${pageSize}`, {
      headers: {
        Authorization: `Bearer ${state.user.accessToken}`
      }
    })
      .then((result) => {
        const responseData = result.data.records || result.data
        const newOrders = Array.isArray(responseData) ? responseData : []
        const totalCount = result.data.totalRecords || newOrders.length

        if (append) {
          const currentUserOrders = Array.isArray(userOrdersRef.current) ? userOrdersRef.current : []
          const hasMore = totalCount > page * pageSize
          setCompletedPagination({ page, pageSize, totalCount, hasMore })
          dispatch({ type: 'SET_USER_ORDERS', userOrders: [...currentUserOrders, ...newOrders] })
        } else {
          const hasMore = totalCount > page * pageSize
          setCompletedPagination({ page, pageSize, totalCount, hasMore })
          return newOrders
        }
      }).catch((err) => {
        toast(err.response?.data?.message || 'Fejl ved indlæsning af afsluttede ordrer')
        throw err
      }))
  }, [state.user.accessToken, cancellablePromise, dispatch])

  useEffect(() => {
    if (state.user.accessToken && state.user.branchId && state.user.id) {
      const pageSize = 12
      const page = 1

      let ongoingOrders = []
      let completedOrders = []
      let ongoingLoaded = false
      let completedLoaded = false

      const updateOrders = () => {
        if (ongoingLoaded && completedLoaded) {
          const allOrders = [...ongoingOrders, ...completedOrders]
          setOrdersLoaded(true)
          dispatch({ type: 'SET_USER_ORDERS', userOrders: allOrders })
        }
      }

      // Fetch ongoing orders
      cancellablePromise(axios(`${baseApiURL()}/orders?isIncludeOnlyOngoing=true&page=${0}&pageSize=${0}`, {
        headers: {
          Authorization:
            `Bearer ${state.user.accessToken}`
        }
      })
        .then((result) => {
          const responseData = result.data.records || result.data
          ongoingOrders = Array.isArray(responseData) ? responseData : []
          ongoingLoaded = true
          updateOrders()
        }).catch((err) => {
          ongoingLoaded = true
          setLoading(false)
          toast(err.response?.data?.message || 'Fejl ved indlæsning af igangværende ordrer')
          updateOrders()
        }))

      // Fetch completed orders
      fetchCompletedOrders(page, pageSize, false)
        .then((orders) => {
          completedOrders = orders
          completedLoaded = true
          updateOrders()
        }).catch((err) => {
          completedLoaded = true
          setLoading(false)
          updateOrders()
        })

      cancellablePromise(axios(`${baseApiURL()}/branches/${state.user.branchId}/fixed_trips`, {
        headers: {
          Authorization:
            `Bearer ${state.user.accessToken}`
        }
      })
        .then((result) => {
          dispatch({ type: 'SET_FIXED_TRIPS', fixedTrips: result.data })
          setFixedTrips(result.data);
        }).catch((err) => {
          setLoading(false)

          toast(err.response.data.message)
        }))

      cancellablePromise(axios(`${baseApiURL()}/branches/${state.user.branchId}/predefined_addresses`, {
        headers: {
          Authorization:
            `Bearer ${state.user.accessToken}`
        }
      }).then((result: any) => {
        dispatch({ type: 'SET_ADDRESSES', addresses: result.data })
        setValues({
          ...values,
          pickupAddress: result.data[0],
          comment: result.data[0] ? result.data[0].comment : ''
        })

        setAddresses(result.data)
      }))

      cancellablePromise(axios(`${baseApiURL()}/users/${state.user.id}/accounts`, {
        headers: {
          Authorization:
            `Bearer ${state.user.accessToken}`
        }
      })
        .then((result) => {
          setAccounts([{ label: 'Ingen konto', value: '' }, ...result.data.map(item => ({ label: item.name, value: item.id, defaultAccount: item.defaultAccount }))])
          setAccountId(result.data.find(account => account.defaultAccount) ? result.data.find(account => account.defaultAccount).id : "")
        }).catch((err) => {
          setLoading(false)
          toast(err.response.data.message)
        }))
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cancellablePromise, dispatch, state.user, fetchCompletedOrders])

  useEffect(() => {
    cancellablePromise(axios(`${baseApiURL()}/current_date`)
      .then((result) => {
        updateTime(new Date(result.data))
        setDate(new Date(result.data))
      }).catch((err) => {
        setLoading(false)
        toast(err.response.data.message)
      }))
  }, [cancellablePromise, updateTime])

  useEffect(() => {
    if (!socketData) return;
    const ordersCopy = [...(Array.isArray(state.userOrders) ? state.userOrders : [])]
    if (socketData.id) {
      const orderIndex = ordersCopy.findIndex(o => Number(o.id) === Number(socketData.id))
      if (orderIndex >= 0) {
        ordersCopy.splice(orderIndex, 1, socketData);
        dispatch({ type: 'SET_USER_ORDERS', userOrders: ordersCopy })
        if (socketData.numberOfPeople > 4) {
          if (socketData.state === 'cancelled') {
            dispatch({
              showModal: true, type: 'SHOW_MODAL', modalType: 'reOrder', order: {
                ...socketData, bookingModuleUpdatedAt: bookingModule.updatedAt
              }
            })
          } else if (socketData.state === 'booked' || socketData.state === 'dispatched') {
            dispatch({ showModal: true, type: 'SHOW_MODAL', modalType: 'bigCarFound' })
          }
        }
      } else {
        ordersCopy.unshift(socketData)
        dispatch({ type: 'SET_USER_ORDERS', userOrders: ordersCopy })
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [socketData]);

  useEffect(() => {
    animate();
    orderRef.current.style.width = `${Math.max(Number(width.current), 480)}px`;
    document.addEventListener('mousemove', onMove);
    document.addEventListener('mouseup', onUp);

    function onUp(e) {
      calc(e);
      clicked.current = null;
    }

    function onMove(ee) {
      calc(ee);
      redraw.current = true;
    }

    function animate() {
      requestAnimationFrame(animate);

      if (!redraw.current) return;

      redraw.current = false;

      if (clicked.current && width.current >= '480' && width.current <= '1100') {
        window.localStorage.setItem('width', width.current.toString())
        orderRef.current.style.width = `${width.current}px`;
        return;
      }
    }
    return () => {
      document.removeEventListener('mousemove', onMove);
      document.removeEventListener('mouseup', onUp);
    }
  }, [calc, width])

  const handleTimeChange = useCallback((time) => {
    setTime(time)
    setShouldUpdateTime(false)
  }, [])

  const handleDateChange = useCallback((date) => {
    setDate(date)
  }, [])

  const handleInputChange = (fieldName: string) => (value: any) => {
    const valuesObj = { ...values }

    valuesObj[fieldName] = value
    setValues(valuesObj)
  }

  const createOrder = () => {
    if (requiredFieldsLength < requiredFields.length) {
      const errors = {};
      requiredFields.filter(code => {
        const fields = [...state.bookingModule.detailFields.list, ...state.bookingModule.customerFields.list]
        const matchedField = fields.find(field => field.code === code)

        return matchedField ? !values[matchedField.name] : false
      }).forEach(code => {
        errors[code] = true
      })
      setErrors(errors)
    } else if (Number(values.numberOfPeople) > 4) {
      dispatch({
        type: 'SHOW_MODAL',
        modalType: 'bigCarConfirmation',
        showModal: true,
        order: {
          bookingModuleUpdatedAt: bookingModule.updatedAt,
          requestedPickup: parse(`${format(date, 'yyyy-MM-dd')}:${format(time, 'HH:mm')}`, 'yyyy-MM-dd:HH:mm', new Date()).toISOString(),
          attributes: selectedAttributes,
          accountId: accountId,
          ...values
        }
      })
      clearData()
    }
    else {
      setOrderPending(true)
      axios.post(`${baseApiURL()}/orders`, {
        bookingModuleUpdatedAt: bookingModule.updatedAt,
        tripType: values.fixedTripId ? 'FIXED' : 'METERED',
        fixedTripId: values.fixedTripId,
        requestedPickup: parse(`${format(date, 'yyyy-MM-dd')}:${format(time, 'HH:mm')}`, 'yyyy-MM-dd:HH:mm', new Date()).toISOString(),
        numberOfPeople: Number(values.numberOfPeople),
        pickupAddress: values.pickupAddress,
        destinationAddress: values.destinationAddress,
        reference: values.reference ? values.reference : null,
        project: values.project ? values.project : null,
        numberOfCars: values.numberOfCars ? values.numberOfCars : 1,
        comment: values.comment ? values.comment : null,
        customerName: values.customerName ? values.customerName : null,
        customerPhone: values.customerPhone ? values.customerPhone : null,
        accountId: accountId,
        attributeIds: selectedAttributes,
      }, {
        headers: {
          Authorization:
            `Bearer ${state.user.accessToken}`
        }
      })
        .then((result) => {
          gaEvent(gaEventCategories.orders, gaEvenActions.ordered_trip, gaUserMapper(state), gaOrderedTripMapper(gaOrderTypes.order, values, result.data[0]))
          toast('Ordre oprettet')
          setOrderPending(false)
          clearData()
          dispatch({ type: 'SET_USER_ORDERS', userOrders: ([...result.data, ...(Array.isArray(state.userOrders) ? state.userOrders : [])]) })
        }).catch((err) => {
          setOrderPending(false)
          setLoading(false)
          if (err.response.data.code === 4020) {
            toast('Bookingmodulet er blevet opdateret. Venligst indlæs siden igen')
          } else {
            toast(err.response.data.message)
          }
        })
    }
  }

  const toggleSelectedAddons = (id: number) => {
    if (selectedAttributes.includes(id)) {
      const attributes = [...selectedAttributes]
      const index = selectedAttributes.findIndex(a => a === id)
      attributes.splice(index, 1)
      updateSelectedAttributes(attributes)
    } else {
      updateSelectedAttributes([...selectedAttributes, id])
    }
  }

  const clearData = () => {
    updateSelectedAttributes(propeties.map(propety => propety.id))
    setShouldUpdateTime(true)
    cancellablePromise(axios(`${baseApiURL()}/current_date`, {
      headers: {
        Authorization:
          `Bearer ${state.user.accessToken}`
      }
    })
      .then((result) => {
        updateTime(new Date(result.data))
        setDate(new Date(result.data))
      }).catch((err) => {
        setLoading(false)
        toast(err.response.data.message)
      }))

    setAccountId(accounts.find(account => account.defaultAccount) ? accounts.find(account => account.defaultAccount).value : "")
    setValues({
      numberOfPeople: '4',
      destinationAddress: null,
      pickupAddress: addresses[0] ? addresses[0] : null,
      comment: addresses[0] ? addresses[0].comment : '',
      project: '',
      price: '',
      attributes: [],
      fixedTripId: '',
      customerName: '',
      numberOfCars: '',
      customerPhone: '',
      reference: '',
    })
  }


  const handleValidation = (regexp: RegExp, fieldName: string) => (value: string) => {
    let error = false

    if (fieldName === 'customerPhone') {
      if (!isValidNumberForRegion(value, 'DK')) {
        error = true
      }
    } else {
      if (value && regexp && !regexp.test(value || '')) {
        error = true
      }
    }
    setValidationErrors({
      ...validationErrors,
      [fieldName]: error
    })
  }

  const { detailFields, customerFields, propeties, addons, requiredFields } = useMemo(() => {
    const detailFields = bookingModule.fields.filter((item) => item.category === 'DETAILS')
    const customerFields = bookingModule.fields.filter((item) => item.category === 'CUSTOMER')
    const propeties = bookingModule.attributes.filter(attribute => attribute.required)
    const addons = bookingModule.attributes.filter(attribute => !attribute.required)

    const requiredFields = [...customerFields, ...detailFields].filter(field =>
      field.required &&
      field.code !== 'BE' &&
      field.code !== 'TI' &&
      field.code !== 'DA'
    ).map(field => field.code)

    return { detailFields, customerFields, propeties, addons, requiredFields }
  }, [bookingModule.attributes, bookingModule.fields]
  )

  if ((values.fixedTripId || Number(values.numberOfPeople) > 4) && !requiredFields.find((item) => 'DE' === item.code)) {
    requiredFields.push('DE')
  } else {
    const destinationField = requiredFields.findIndex((val) => val === 'DE')
    if (destinationField !== -1)
      requiredFields.splice(destinationField, 1)
  }

  const requiredFieldsLength = requiredFields.filter(code => {
    const fields = [...state.bookingModule.detailFields.list, ...state.bookingModule.customerFields.list]
    const matchedField = fields.find(field => field.code === code)
    return matchedField ? !!values[matchedField.name] : false
  }).length

  const resetError = (fieldName: string) => {
    setErrors({
      ...errors,
      [fieldName]: false
    })
    setValidationErrors({
      ...validationErrors,
      [fieldName]: false
    })
  }

  const handleSortOrder = () => {
    setSortOrders(!sortOrders)
    orders.current = [...(Array.isArray(state.userOrders) ? state.userOrders : [])]
  }

  const loadMoreCompletedOrders = useCallback(() => {
    if (loadingMoreCompleted || !completedPagination.hasMore) return

    const nextPage = completedPagination.page + 1
    setLoadingMoreCompleted(true)

    fetchCompletedOrders(nextPage, completedPagination.pageSize, true)
      .then(() => {
        setLoadingMoreCompleted(false)
      })
      .catch(() => {
        setLoadingMoreCompleted(false)
      })
  }, [completedPagination, loadingMoreCompleted, fetchCompletedOrders])

  const { completedOrders, orderStatus } = useMemo(() => {
    const completedOrders = (Array.isArray(state.userOrders) ? state.userOrders : []).filter(order =>
      order.state === 'completed' ||
      order.state === 'cancelled' ||
      order.state === 'concluded' ||
      order.state === 'noshow' ||
      order.state === 'deleted')

    const orderStatus: Record<string, any> = {
      created: {
        label: 'Anmoder',
        style: 'statusWhite',
      },
      booked: {
        label: 'Bestilt',
        style: 'textGreen',
      },
      dispatching: {
        label: 'Søger ledig taxi',
        style: 'statusBlue',
      },
      blocked: {
        label: 'Forsinket',
        style: 'statusYellow'
      },
      arriving: {
        label: 'På vej',
        style: 'statusGreen',
        statusText: 'textGreen'
      },
      taximeter_started: {
        style: 'statusBlue',
        label: 'Afventer passager'
      },
      noshow: {
        label: 'Ingen passager',
        style: 'statusYellow'
      },
      trip_started: {
        label: 'Kører',
        style: 'statusGrey'
      },
      completed: {
        label: 'Afsluttet',
        style: 'white'
      },
      concluded: {
        label: 'Afsluttet',
        style: 'statusWhite'
      },
      cancelled: {
        label: 'Annulleret',
        style: 'statusRed'
      },
      deleted: {
        label: 'Annulleret',
        style: 'statusRed'
      }
    }

    return { completedOrders, orderStatus }
  }, [state.userOrders])

  const { onGoingOrders } = useMemo(() => {
    const filteredOnGoingOrders = (Array.isArray(state.userOrders) ? state.userOrders : []).filter(order =>
      order.state !== 'completed' &&
      order.state !== 'cancelled' &&
      order.state !== 'concluded' &&
      order.state !== 'noshow' &&
      order.state !== 'deleted')

    if (sortOrders) {
      filteredOnGoingOrders.sort((a, b) => new Date(b.requestedPickup).valueOf() - new Date(a.requestedPickup).valueOf())
    } else {
      filteredOnGoingOrders.sort((a, b) => new Date(a.requestedPickup).valueOf() - new Date(b.requestedPickup).valueOf())
    }

    const onGoingOrders = filteredOnGoingOrders
    return { onGoingOrders }
  }, [state.userOrders, sortOrders])

  return (
    <>
      <Header />
      <div className={styles.container}>
        <div className={styles.order} ref={orderRef}>
          <button className={styles.draggableButton} onMouseDown={(e) => {
            clicked.current = true
            calc(e)
          }} />
          <header className={styles.header}>
            {!loading && <> <h3 className='tw-mr-3'>Bestil tur</h3>
              <button className={styles.clearFormButton} onClick={() => clearData()}>Nulstil</button>
              <div className='tw-flex tw-ml-auto'>
                <DropDown sortOptions={false} options={fixedTrips.map(item => ({ label: item.title, value: item }))} modifiers='tw-ml-3 tw-flex-1' handleOnChange={(fixedtrip) => {
                  setValues({
                    fixedTripId: fixedtrip.id,
                    accountId: fixedtrip.account ? fixedtrip.account.id : "",
                    ...fixedtrip
                  })
                  setAccountId(fixedtrip.account ? fixedtrip.account.id : "")
                  updateSelectedAttributes(fixedtrip.attributes)
                }
                } placeholder='Vælg fast tur' value={values.fixedTripId} />
              </div>
            </>}
          </header>
          {propeties.length > 0 ? <div className={styles.attributes}>
            {!loading && <ul className='tw-flex tw-flex-auto tw-items-center tw-flex-wrap'>
              {propeties.filter(item => item.required)
                .map((item, index) =>
                  <li className={styles.attributeItem} key={index}>
                    <span className='tw-w-4 tw-h-4 tw-mr-2'><svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                      <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                    </svg>
                    </span>
                    <span>{item.description}</span>
                  </li>)}
            </ul>}
          </div> : null}
          <div className={styles.content}>
            {loading ?
              <div className='animate-pulse'>
                <div className={styles.section}>
                  <div className='tw-flex-1 tw-mr-4 input-field-loading' />
                  <div className='tw-flex-1 tw-mr-4 input-field-loading' />
                  <div className='tw-flex-2 input-field-loading' />
                </div>
                <div className={styles.section}>
                  <div className='tw-flex-full tw-mb-9 input-field-loading' />
                  <div className='tw-flex-full input-field-loading' />
                </div>
                <div className={styles.section}>
                  <div className='label-loading label-loading--small tw-mb-2' />
                  <div className='tw-flex tw-flex-wrap tw-flex-full'>
                    <div className={`tw-flex-2 tw-mr-4 input-field-loading`} />
                    <div className={`tw-flex-2 tw-mr-4 input-field-loading`} />
                  </div>
                  <div className='tw-flex-1' />
                </div>
                <div className={styles.section}>
                  <div className='label-loading label-loading--small tw-mb-5' />
                  <div className='tw-flex tw-flex-wrap tw-flex-full tw-mb-8'>
                    <div className={`tw-flex-2 tw-mr-4 input-field-loading`} />
                    <div className={`tw-flex-2 tw-mr-4 input-field-loading`} />
                  </div>
                  <div className='tw-flex tw-flex-wrap tw-flex-full tw-mb-8'>
                    <div className={`tw-flex-2 tw-mr-4 input-field-loading`} />
                    <div className={`tw-flex-2 tw-mr-4 input-field-loading`} />
                  </div>
                  <div className='tw-flex tw-flex-wrap tw-flex-full'>
                    <div className={`tw-flex-2 tw-mr-4 input-field-loading`} />
                    <div className={`tw-flex-2 tw-mr-4 input-field-loading`} />
                  </div>
                </div>
              </div> : <>
                <div className={styles.section}>
                  {state.bookingModule.detailFields.list.filter((propety) => detailFields.find((item) => propety.code === item.code))
                    .map((item, index) => {
                      switch (item.type) {
                        case 'autocomplete':
                          return <div key={index} className={`tw-mb-5 ${item.modifier ? item.modifier : ''} `}>
                            <AutocompletePoi
                              readOnly={!!values.fixedTripId}
                              completedAddress={values[item.name]}
                              predefinedAddresses={addresses}
                              error={errors[item.code]}
                              resetError={() => resetError(item.code)}
                              label={`
                        ${item.inputLabel || item.description}
                         ${(detailFields.find((field) => field.code === item.code).required || (values.fixedTripId || Number(values.numberOfPeople) > 4)) ? '*' : ''}`}
                              placeholder={item.placeholder}
                              iconType={item.name === 'pickupAddress' ? 'pickup' : 'destination'}
                              setAddress={() => handleInputChange(item.name)}
                              name={item.name} />
                            {errors[item.code] && <p className='errorMsg'>Mangler at udfylde {(item.inputLabel || item.description).toLocaleLowerCase()}</p>}

                          </div>
                        case 'dropdownInput':
                          return <div key={index} className={`tw-mb-3 tw-mr-3 last:mr-0 ${item.modifier ? item.modifier : ''} `}>
                            <DropDownInput
                              readOnly={!!values.fixedTripId}
                              label={`${item.inputLabel || item.description} ${detailFields.find((field) => field.code === item.code).required ? '*' : ''}`}
                              name={item.name}
                              curried={true}
                              defaultInputValue={values.numberOfCars}
                              defaultDropDownValue={values[item.name] ? Number(values[item.name]) : 4}
                              options={item.dropDownOptions}
                              handleInputChange={() => handleInputChange('numberOfCars')}
                              handleDropDownChange={() => handleInputChange(item.name)} />
                            {errors[item.code] && <p className='errorMsg'>Mangler at udfylde {(item.inputLabel || item.description).toLocaleLowerCase()}</p>}
                          </div>
                        case 'date':
                          return (
                            <div key={index} className={`tw-mb-3 tw-mr-3 last:mr-0 ${item.modifier ? item.modifier : ''} `}>
                              <DatePickerComponent
                                label={`${item.inputLabel || item.description} ${detailFields.find((field) => field.code === item.code).required ? '*' : ''}`}
                                selected={date}
                                minDate={new Date()}
                                onChange={handleDateChange}
                              />
                              {errors[item.code] && <p className='errorMsg'>Mangler at udfylde {(item.inputLabel || item.description).toLocaleLowerCase()}</p>}
                            </div>)
                        case 'time':
                          return <div key={index} className={`tw-mb-3 tw-mr-3 last:mr-0 ${item.modifier ? item.modifier : ''} `}>
                            <TimeInputComponent
                              value={time}
                              handleChange={handleTimeChange}
                              label={`
                         ${item.inputLabel || item.description}
                          ${detailFields.find((field) => field.code === item.code).required ? '*' : ''}`} />
                            {errors[item.code] && <p className='errorMsg'>Mangler at udfylde {(item.inputLabel || item.description).toLocaleLowerCase()}</p>}
                          </div>
                        default:
                          return <div key={index} className={`tw-mb-3 tw-mr-3 last:mr-0 ${item.modifier ? item.modifier : ''} `}>
                            <Input
                              placeholder={item.placeholder}
                              type={item.type}
                              value={values[item.name]}
                              handleValidation={() => handleValidation(item.validation, item.name)}
                              error={validationErrors[item.name]}
                              readOnly={!!values.fixedTripId}
                              min={format(new Date(), item.type === 'date' ? 'yyyy-MM-dd' : 'HH:mm')}
                              handleInputChange={() => handleInputChange(item.name)}
                              IconRight={item.IconRight}
                              countryCode={item.countryCode}
                              label={`
                        ${item.inputLabel || item.description}
                         ${detailFields.find((field) => field.code === item.code).required ? '*' : ''}`}
                              name='' />
                            {errors[item.code] && <p className='errorMsg'>Mangler at udfylde {(item.inputLabel || item.description).toLocaleLowerCase()}</p>}
                          </div>
                      }
                    })}
                  {(values.fixedTripId || Number(values.numberOfPeople) > 4) && !detailFields.find((item) => 'DE' === item.code) && <AutocompletePoi
                    label='Destination *'
                    placeholder='Vælg destination'
                    iconType='destination'
                    readOnly={!!values.fixedTripId}
                    modifiers='tw-mb-5 tw-flex-full'
                    setAddress={() => handleInputChange('destinationAddress')}
                    completedAddress={values.destinationAddress}
                    name='destinationAddress'
                  />}
                </div>
                {addons && addons.length > 0 && <div className={styles.section}>
                  <h6 className='tw-flex-full tw-mb-3'>Tilvalg</h6>
                  {addons.map((item, index) => (
                    <Button
                      modifiers='tw-mr-3 first:ml-0 tw-mb-3'
                      disabled={!!values.fixedTripId}
                      handleClick={() => toggleSelectedAddons(item.id)}
                      label={item.description}
                      type={(values.attributes && values.attributes.find((attribute: any) => attribute === item.id) ? 'addonActive' : selectedAttributes.includes(item.id)) ? 'addonActive' : 'addon'}
                      key={index} />
                  ))}
                </div>
                }
                <div className={styles.section}>
                  <h6 className='tw-flex-full tw-mb-3'>Kunde informationer</h6>
                  {accounts && accounts.length > 1 &&
                    <DropDown
                      placeholder='Vælg konto'
                      value={accountId}
                      label='Konto'
                      modifiers='tw-flex-full tw-mb-5'
                      options={accounts}
                      handleOnChange={setAccountId}
                    />}
                  {state.bookingModule.customerFields.list.filter((propety) => customerFields.find((item) => propety.code === item.code))
                    .map((item, index) => {
                      if (item.type === 'dropdown') {
                        return <div key={index} className={`tw-mb-5 tw-mr-3 last:mr-0 ${item.modifier ? item.modifier : ''} `}>
                          <DropDown
                            key={index}
                            label={item.inputLabel || item.description}
                            curried={true}
                            value={accountId ? 'card' : 'cash'}
                            options={item.dropDownOptions}
                          />
                          {errors[item.code] && <p className='errorMsg'>Mangler at udfylde {(item.inputLabel || item.description).toLocaleLowerCase()}</p>}

                        </div>
                      } else if (item.type === 'textarea') {
                        return <div key={index} className={`tw-mb-5 tw-mr-3 last:mr-0 ${item.modifier ? item.modifier : ''} `}>
                          <TextArea
                            name='test'
                            resetError={() => resetError(item.code)}
                            error={errors[item.code]}
                            label={`
                     ${item.inputLabel || item.description}
                     ${customerFields.find((field) => field.code === item.code).required ? '*' : ''} `}
                            handleInputChange={() => handleInputChange(item.name)}
                            value={values.comment}
                            placeholder={item.placeholder}
                          />
                          {errors[item.code] && <p className='errorMsg'>Mangler at udfylde {(item.inputLabel || item.description).toLocaleLowerCase()}</p>}
                        </div>
                      }
                      return <div key={index} className={`tw-mr-3 tw-mb-3 ${item.modifier ? item.modifier : ''} `}>
                        <Input
                          placeholder={item.placeholder}
                          key={index}
                          type='text'
                          isAllowOnlyDigits={item.name === 'customerPhone'}
                          value={values[item.name]}
                          handleValidation={() => handleValidation(item.validation, item.name)}
                          error={validationErrors[item.name] || errors[item.code]}
                          handleInputChange={() => handleInputChange(item.name)}
                          IconRight={item.IconRight}
                          countryCode={item.countryCode}
                          resetError={() => resetError(item.code)}
                          label={`
                    ${item.code === 'RE' ? bookingModule.referenceLabel : item.inputLabel || item.description}
                    ${customerFields.find((field) => field.code === item.code).required ? '*' : ''} `}
                          name={item.name} />
                        {errors[item.code] && <p className='errorMsg'>Mangler at udfylde {(item.inputLabel || item.description).toLocaleLowerCase()}</p>}
                        {validationErrors.customerPhone && item.name === 'customerPhone' && <p className='errorMsg'>Ugyldigt telefonnummer</p>}
                      </div>
                    })}
                </div>
              </>}
          </div>
          <footer className={styles.footer}>
            {!loading && <>
              {values.price && <p className={styles.price}><span>Total</span> <span>{values.price} kr.</span></p>}
              <Button loading={orderPending} disabled={(!!Object.values(validationErrors).filter(error => error).length || !!Object.values(errors).filter(error => error).length)} modifiers={values.price ? '' : 'tw-ml-auto'} label='Bestil tur' type='primary' handleClick={createOrder} />  </>}
          </footer>
        </div>
        <div className={styles.orderStatus} style={orderRef.current ? { maxHeight: orderRef.current.clientHeight } : {}}>
          <div className={styles.onGoingOrders}>
            {loading ?
              <div className='animate-pulse animation-delay-1'>
                <div className={`label-loading tw-mb-11`} />
                <div className={`box-loading tw-opacity-80`} />
                <div className={`box-loading tw-opacity-60`} />
                <div className={`box-loading tw-opacity-40`} />
                <div className={`box-loading tw-opacity-20`} />
              </div>
              : <>
                <div className='tw-flex tw-items-center'>
                  <h5 className='tw-font-medium'>Bestilte/igangværende ture <span className='tw-opacity-50 tw-ml-2'>{onGoingOrders.length}</span></h5>
                </div>
                <Button modifiers={styles.subtitle} size='small' type='minimal' handleClick={handleSortOrder}>Senest opdateret <span className={`${styles.sortArrow} ${sortOrders ? styles.sortArrowActive : ''}`}><svg xmlns="http://www.w3.org/2000/svg" className="tw-h-5 tw-w-5" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" clipRule="evenodd" />
                </svg></span>
                </Button>
                <div className={styles.boxesContainer}>
                  {onGoingOrders.map((order, index) => {
                    return (
                      <div key={index} className={styles.box} onClick={() => dispatch({ type: 'SHOW_ASIDE', asideType: 'OrderDetails', show: true, orderId: order.id, referenceLabel: bookingModule.referenceLabel })}>
                        <header className='tw-flex'>
                          <div>
                            {order.state === 'booked' && <p className={styles.requestedPickup}>{format(new Date(order.requestedPickup), 'dd/MM, HH:mm')}</p>}
                            {order.estimatedPickup && <span className={styles[orderStatus[order.state] ? orderStatus[order.state].statusText : '']}>{differenceInMinutes(new Date(order.estimatedPickup), state.date) === 0 ? 'Mindre end 1 minut' : (differenceInMinutes(new Date(order.estimatedPickup), state.date) > 10 ? 'Indenfor 10 min' : `Indenfor ${differenceInMinutes(new Date(order.estimatedPickup), state.date)} min`)}</span>}
                            {order.reference && <h6 className={styles.boxTitle}>{order.reference}</h6>}
                            <h6 className={`${order.reference ? styles.boxTitleSmall : styles.boxTitle} tw-mb-2`}>{order.customerName}</h6>
                          </div>
                          <span className={`${styles.status} ${styles[orderStatus[order.state] ? orderStatus[order.state].style : '']} `}>{orderStatus[order.state] && orderStatus[order.state].label ? orderStatus[order.state].label : ''}</span>
                        </header>
                        <p className={`${styles.address} tw-mb-2`}>
                          <svg className='tw-w-4 tw-h-4  tw-mr-1' xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                            <path fillRule="evenodd" d="M5.05 4.05a7 7 0 119.9 9.9L10 18.9l-4.95-4.95a7 7 0 010-9.9zM10 11a2 2 0 100-4 2 2 0 000 4z" clipRule="evenodd" />
                          </svg>
                          {order.pickupAddress ? order.pickupAddress.poi ? order.pickupAddress.poi : order.pickupAddress.fullAddress : null}
                        </p>
                        {order.destinationAddress && <p className={`${styles.address} tw-opacity-50`}>
                          <svg className='tw-w-4 tw-h-4 tw-mr-1' xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                            <path fillRule="evenodd" d="M3 6a3 3 0 013-3h10a1 1 0 01.8 1.6L14.25 8l2.55 3.4A1 1 0 0116 13H6a1 1 0 00-1 1v3a1 1 0 11-2 0V6z" clipRule="evenodd" />
                          </svg>
                          {order.destinationAddress ? order.destinationAddress.poi ? order.destinationAddress.poi : order.destinationAddress.fullAddress : null}
                        </p>}
                      </div>
                    )
                  })}
                </div>
              </>}
          </div>
          <div className={styles.onGoingOrders}>
            {loading ?
              <div className='animate-pulse animation-delay-2'>
                <div className={`label-loading tw-mb-11`} />
                <div className={`box-loading`} />
                <div className={`box-loading tw-opacity-80`} />
                <div className={`box-loading tw-opacity-60`} />
                <div className={`box-loading tw-opacity-40`} />
                <div className={`box-loading tw-opacity-20`} />
              </div>
              : <>
                <div className='tw-flex tw-items-center tw-mb-5'>
                  <h5 className='tw-font-medium'>Afsluttede ture <span className='tw-opacity-50 tw-ml-2'>{completedOrders.filter(order => order.state === 'completed' || order.state === 'concluded').length}</span> </h5>
                  <div className={styles.completedOrders}>
                    <span className='tw-text-sm'>{completedOrders.filter(order => order.state === 'cancelled').length}</span>
                    <svg xmlns="http://www.w3.org/2000/svg" className="tw-h-5 tw-w-5 tw-ml-3" viewBox="0 0 20 20" fill="currentColor">
                      <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                    </svg>
                  </div>
                </div>
                <Button size='small' type='minimal' handleClick={() => dispatch({ showModal: true, type: 'SHOW_MODAL', modalType: 'clearOrders' })} label='Ryd afsluttede' modifiers={styles.clearOrders} />
                <div className={styles.boxesContainer}>
                  {completedOrders.map((order, index) => (
                    <div className={styles.box} key={index} onClick={() => dispatch({ type: 'SHOW_ASIDE', asideType: 'OrderDetails', show: true, orderId: order.id, referenceLabel: bookingModule.referenceLabel })}>
                      <header className='tw-flex'>
                        <div>
                          {order.state === 'cancelled' && <button onClick={(e) => {
                            e.stopPropagation()
                            dispatch({ showModal: true, type: 'SHOW_MODAL', modalType: 'reOrder', order: { ...order, bookingModuleUpdatedAt: bookingModule.updatedAt } })
                          }}
                            className={styles.reOrder}>Genbestil tur</button>}
                          {order.actualCompleted && <p className={styles.orderFinishedDate}>
                            <svg className='tw-w-5 tw-h-5 tw-mr-3' xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                              <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                            </svg>
                            <span>{format(new Date(order.actualCompleted), 'dd/MM, HH:mm')}</span>
                          </p>}
                          {order.estimatedCompleted && <p className={styles.orderEstimatedFinishedDate}>
                            <svg className='tw-w-5 tw-h-5 tw-mr-3' xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                              <path fillRule="evenodd" d="M3 6a3 3 0 013-3h10a1 1 0 01.8 1.6L14.25 8l2.55 3.4A1 1 0 0116 13H6a1 1 0 00-1 1v3a1 1 0 11-2 0V6z" clipRule="evenodd" />
                            </svg>
                            <span>{format(new Date(order.estimatedCompleted), 'HH:mm')}</span>
                          </p>
                          }
                          {order.reference && <h6 className={styles.boxTitle}>{order.reference}</h6>}
                          <h6 className={`${order.reference ? styles.boxTitleSmall : styles.boxTitle} tw-mb-2`}>{order.customerName}</h6>
                        </div>
                        <span className={`${styles.status} ${styles[orderStatus[order.state] ? orderStatus[order.state].style : '']} `}>{orderStatus[order.state].label ? orderStatus[order.state].label : ''}</span>
                      </header>
                      <p className={`${styles.address} tw-opacity-50 tw-mb-2`}>
                        <svg className='tw-w-4 tw-h-4  tw-mr-1' xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                          <path fillRule="evenodd" d="M5.05 4.05a7 7 0 119.9 9.9L10 18.9l-4.95-4.95a7 7 0 010-9.9zM10 11a2 2 0 100-4 2 2 0 000 4z" clipRule="evenodd" />
                        </svg>
                        {order.pickupAddress ? order.pickupAddress.poi ? order.pickupAddress.poi : order.pickupAddress.fullAddress : null}
                      </p>
                      {order.destinationAddress && <p className={`${styles.address}`}>
                        <svg className='tw-w-4 tw-h-4 tw-mr-1' xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                          <path fillRule="evenodd" d="M3 6a3 3 0 013-3h10a1 1 0 01.8 1.6L14.25 8l2.55 3.4A1 1 0 0116 13H6a1 1 0 00-1 1v3a1 1 0 11-2 0V6z" clipRule="evenodd" />
                        </svg>
                        {order.destinationAddress ? order.destinationAddress.poi ? order.destinationAddress.poi : order.destinationAddress.fullAddress : null}
                      </p>}
                    </div>
                  ))}
                </div>
                {completedPagination.hasMore && (
                  <div className='tw-mt-4 tw-flex tw-justify-center'>
                    <Button
                      loading={loadingMoreCompleted}
                      disabled={loadingMoreCompleted}
                      label='Indlæs flere'
                      type='minimal'
                      size='small'
                      handleClick={loadMoreCompletedOrders}
                    />
                  </div>
                )}
              </>}
          </div>
        </div>
      </div>
    </>
  )
}
export default Order